import React, { useContext, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  IonContent,
  IonPage,
  IonButton,
  IonIcon,
  IonFooter,
  IonText,
  useIonViewWillEnter,
  useIonViewDidLeave,
} from "@ionic/react";
import { logOutOutline } from "ionicons/icons";
import { AuthenticationContext } from "../../providers/AuthenticationProvider/AuthenticationProvider";
import {
  getNotificationLocalStorage,
  hasNotificationBlocked,
  hasNotificationFunctionality,
  hasNotificationPermission,
  isInstalled,
  isIOs,
} from "../../utils/device";
import NotificationButton from "../../components/NotificationButton/NotificationButton";
import { FormattedDate } from "react-intl";

// Firestore Timestamps carry `.seconds` (client SDK) or `._seconds` (serialized).
const tsToSeconds = (ts) => ts?.seconds ?? ts?._seconds ?? null;

// One dated expiry (certificate, subscription, membership). Renders nothing when
// the date is missing; flags it in red once it is in the past.
const ExpiryInfo = ({ label, value }) => {
  const seconds = tsToSeconds(value);
  if (!seconds) {
    return null;
  }
  const isExpired = seconds * 1000 < Date.now();
  return (
    <div style={{ marginTop: "1rem" }}>
      <h3 style={{ fontWeight: "700" }}>{label}</h3>
      <IonText color={isExpired ? "danger" : undefined}>
        <FormattedDate
          value={seconds * 1000}
          day="2-digit"
          month="long"
          year="numeric"
        />
        {isExpired && " (scaduto)"}
      </IonText>
    </div>
  );
};

ExpiryInfo.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.object,
};

const NotificationSection = () => {
  if ((isIOs() && !isInstalled()) || !hasNotificationFunctionality()) {
    return null;
  }
  const hasAccepted =
    getNotificationLocalStorage() === "true" && hasNotificationPermission();
  return (
    <div className="NotificationSection">
      <h3 style={{ fontWeight: "700" }}>Notifiche</h3>
      {hasAccepted && (
        <p>
          <IonText color="success">Le notifiche sono attive</IonText>
        </p>
      )}

      {hasNotificationBlocked() && (
        <p>
          <IonText color="danger">
            Le notifiche sono bloccate.
            <br />
            Riattivale nelle impostazioni del tuo dispositivo
          </IonText>
        </p>
      )}

      {!hasAccepted && !hasNotificationBlocked() && (
        <p className="NotificationMessage">
          <IonText color="danger">Le notifiche sono disattivate</IonText>
          <br />
          {hasNotificationFunctionality() && (
            <NotificationButton>Abilita</NotificationButton>
          )}
        </p>
      )}
    </div>
  );
};

const Profile = () => {
  const { logOutUser, user, userDetails } = useContext(AuthenticationContext);
  const [dateLastNotificationCheck, setDateLastNotificationCheck] = useState(
    new Date(),
  );
  const timerHanlder = useRef(null);
  useIonViewWillEnter(() => {
    // start a polling to check if the user has accepted the notifications
    timerHanlder.current = setInterval(() => {
      console.log("Checking notifications");
      setDateLastNotificationCheck(new Date());
    }, 1000);
    return () => clearInterval(timerHanlder.current);
  });

  useIonViewDidLeave(() => {
    if (timerHanlder.current) {
      clearInterval(timerHanlder.current);
    }
  });

  return (
    <IonPage>
      <IonContent className="ion-text-center ion-padding">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            marginTop: "50px",
          }}
        >
          <img src="/tennis-player_3295395.png" alt="Avatar" width="150" />
          <h3
            style={{ fontWeight: "bold" }}
          >{`${userDetails?.nome} ${userDetails?.cognome}`}</h3>
          <h5>{user?.email}</h5>
        </div>
        <ExpiryInfo
          label="Scadenza certificato medico"
          value={userDetails?.certificate_expire}
        />
        <ExpiryInfo
          label="Scadenza abbonamento"
          value={userDetails?.economic_subscription_expire}
        />
        <ExpiryInfo
          label="Scadenza iscrizione"
          value={userDetails?.subscription_expire}
        />
        <NotificationSection key={dateLastNotificationCheck.getTime()} />
      </IonContent>
      <IonFooter className="ion-no-border ion-padding">
        <IonButton
          expand="block"
          size="large"
          color="danger"
          onClick={() => logOutUser()}
        >
          <IonIcon slot="start" icon={logOutOutline} />
          Logout
        </IonButton>
      </IonFooter>
    </IonPage>
  );
};

export default Profile;
